import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import {
  Group as GroupIcon,
  Check as AcceptIcon,
  Close as DeclineIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { useGroups } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function UserInvitations() {
  const { currentUser } = useAuth();
  const { fetchUserInvitations, acceptInvitation, declineInvitation, refreshGroups } = useGroups();
  const navigate = useNavigate();
  const theme = useTheme();

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadInvitations = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const userInvitations = await fetchUserInvitations();
      setInvitations(userInvitations);
    } catch (error) {
      console.error('UserInvitations: Error loading invitations:', error);
      setError('Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, fetchUserInvitations]);

  useEffect(() => {
    loadInvitations();
  }, [currentUser, loadInvitations]);

  const handleAcceptInvitation = async (invitationId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await acceptInvitation(invitationId);
      setSuccess('Invitation accepted! Redirecting...');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      await refreshGroups();
      setTimeout(() => navigate('/groups'), 2000);
    } catch (error) {
      setError(error.message || 'Failed to accept invitation.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await declineInvitation(invitationId);
      setSuccess('Invitation declined.');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (error) {
      setError(error.message || 'Failed to decline invitation.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Recently';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  if (!currentUser) return null;

  const TicketCard = ({ invitation }) => (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        mb: 3,
        filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.3))',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)'
        }
      }}
    >
      {/* Main Ticket Body */}
      <Box
        sx={{
          flex: 1,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(10px)',
          borderRadius: { xs: '16px 16px 0 0', sm: '16px 0 0 16px' },
          p: 3,
          border: `1px solid ${alpha('#fff', 0.1)}`,
          borderRight: { sm: '2px dashed rgba(255,255,255,0.2)' },
          borderBottom: { xs: '2px dashed rgba(255,255,255,0.2)', sm: 'none' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main'
            }}
          >
            <GroupIcon />
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              INVITATION TO JOIN
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {invitation.groupName}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          "{invitation.groupDescription || 'No description provided'}"
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" display="block" color="text.secondary">INVITED BY</Typography>
              <Typography variant="body2">{invitation.invitedByEmail}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" display="block" color="text.secondary">DATE</Typography>
              <Typography variant="body2">{formatDate(invitation.invitedAt)}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Ticket Stub (Actions) */}
      <Box
        sx={{
          width: { xs: '100%', sm: '180px' },
          bgcolor: alpha(theme.palette.primary.main, 0.9),
          borderRadius: { xs: '0 0 16px 16px', sm: '0 16px 16px 0' },
          p: 3,
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative Circles for "Perforation" */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 'auto', sm: -10 },
            top: { xs: -10, sm: 'auto' },
            bottom: { xs: 'auto', sm: '50%' },
            transform: { sm: 'translateY(50%)' },
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: theme.palette.background.default
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: { xs: 'auto', sm: -10 },
            top: { xs: -10, sm: 'auto' },
            bottom: { xs: 'auto', sm: '50%' },
            transform: { sm: 'translateY(50%)' },
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: theme.palette.background.default
          }}
        />

        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', sm: 'block' }, textAlign: 'center' }}>
          ADMIT ONE
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
          <Tooltip title="Accept">
            <IconButton
              sx={{ bgcolor: 'white', '&:hover': { bgcolor: alpha('#fff', 0.9) } }}
              onClick={() => handleAcceptInvitation(invitation.id)}
              disabled={loading}
            >
              <AcceptIcon color="success" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Decline">
            <IconButton
              sx={{ bgcolor: 'rgba(0,0,0,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.3)' } }}
              onClick={() => handleDeclineInvitation(invitation.id)}
              disabled={loading}
            >
              <DeclineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Pending Invitations ({invitations.length})
        </Typography>
        <Button
          size="small"
          onClick={loadInvitations}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading && invitations.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : invitations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'transparent', border: '2px dashed rgba(255,255,255,0.1)' }}>
          <TicketIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Pending Invitations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have any pending group invitations at the moment.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {invitations.map((invitation) => (
            <TicketCard key={invitation.id} invitation={invitation} />
          ))}
        </Box>
      )}
    </Box>
  );
}
